// A CONFERÊNCIA DA BASE LÍQUIDA LEGAL de um órgão (#273, ADR-0012 decisão 3).
//
// Avisa, nunca trava. Mostra, na última competência fechada, quantos servidores
// ativos têm base legal calculável, o que falta para o portão deixar uma regra
// legal valer (95% de cobertura e um desconto obrigatório classificado), as
// rubricas do contracheque ainda sem classificação e, servidor a servidor, a
// base informada (salário da carga) contra a legal.
//
// A lista é CORTADA nas maiores diferenças: é para achar onde a base informada
// mais se afasta da lei, não para auditar a folha inteira — quem precisa de
// tudo tem a ficha de cada servidor.

import { useQuery } from "@tanstack/react-query";
import { Card, DataTable, fmtBRL, type Column } from "@atlas/ui/web";
import type { ConferenciaBaseLegal } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { mensagemDeErro } from "../../lib/erro-validacao";
import { ROTULO_BASE, textoDoImpedimento } from "./regra-margem-modal";

type LinhaServidor = ConferenciaBaseLegal["servidores"][number];

/** Quantas linhas a conferência mostra. */
export const LIMITE_LINHAS_CONFERENCIA = 50;

const ROTULO_MOTIVO: Record<NonNullable<LinhaServidor["motivo"]>, string> = {
  sem_holerite: "Sem contracheque gravado",
  sem_provento: "Sem provento que componha a base",
  rubrica_sem_classificacao: "Rubrica a classificar",
};

const fmtMes = (c: string | null) => (c ? `${c.slice(4)}/${c.slice(0, 4)}` : "—");

export function PainelBaseLegal({ unidadeId }: { unidadeId: number }) {
  const q = useQuery({
    queryKey: ["admin", "regras-margem", "base-legal", unidadeId],
    queryFn: () => atlas.admin.regrasMargem.baseLegal(unidadeId),
  });

  if (q.isError) {
    return (
      <Card>
        <div role="alert">{mensagemDeErro(q.error, "Não foi possível medir a base legal.")}</div>
      </Card>
    );
  }
  const d = q.data;

  // Primeiro quem está sem base (fica sem margem sob a regra legal), depois as
  // maiores diferenças entre a base informada e a legal.
  const linhas = [...(d?.servidores ?? [])]
    .sort((a, b) => {
      if ((a.baseLegal === null) !== (b.baseLegal === null)) return a.baseLegal === null ? -1 : 1;
      const da = Math.abs(a.baseInformada - (a.baseLegal ?? 0));
      const db = Math.abs(b.baseInformada - (b.baseLegal ?? 0));
      return db - da;
    })
    .slice(0, LIMITE_LINHAS_CONFERENCIA);

  const colunas: Column<LinhaServidor>[] = [
    { key: "matricula", header: "Matrícula", mono: true, render: (s) => s.matricula },
    { key: "nome", header: "Servidor", wrap: true, render: (s) => s.nome },
    { key: "informada", header: "Base informada", align: "right", render: (s) => fmtBRL(s.baseInformada) },
    {
      key: "legal",
      header: "Base legal",
      align: "right",
      render: (s) => (s.baseLegal === null ? "—" : fmtBRL(s.baseLegal)),
    },
    {
      key: "diferenca",
      header: "Diferença",
      align: "right",
      render: (s) => (s.baseLegal === null ? "—" : fmtBRL(s.baseLegal - s.baseInformada)),
    },
    {
      key: "motivo",
      header: "Sem base porque",
      wrap: true,
      render: (s) =>
        s.motivo === null
          ? ""
          : `${ROTULO_MOTIVO[s.motivo]}${s.rubricasSemClassificacao.length > 0 ? `: ${s.rubricasSemClassificacao.join(", ")}` : ""}`,
    },
  ];

  return (
    <Card>
      <div style={{ display: "grid", gap: 12 }}>
        <div>
          <div style={{ fontWeight: 700 }}>Conferência da base líquida legal</div>
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2 }}>
            Base da regra vigente: {d ? ROTULO_BASE[d.baseDaRegraVigente] : "…"}. Medida no contracheque da última
            competência fechada ({fmtMes(d?.competencia ?? null)}). Só avisa: nada aqui muda margem.
          </div>
        </div>

        {d ? (
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 13 }}>
            <div>
              <div style={{ color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase" }}>Cobertura</div>
              <strong>
                {(d.cobertura * 100).toFixed(1)}% · {d.comBase} de {d.ativos} ativos
              </strong>
            </div>
            <div>
              <div style={{ color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase" }}>
                Descontos obrigatórios classificados
              </div>
              <strong>{d.vinculosDescontoObrigatorio}</strong>
            </div>
          </div>
        ) : null}

        {d && d.impedimentos.length > 0 ? (
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Para a base legal poder valer, falta:</div>
            <ul aria-label="O que falta para a base legal" style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 13 }}>
              {d.impedimentos.map((i) => (
                <li key={i.codigo}>{textoDoImpedimento(i)}</li>
              ))}
            </ul>
          </div>
        ) : d ? (
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
            A cobertura permite cadastrar uma regra na base líquida legal.
          </div>
        ) : null}

        {d && d.rubricasSemClassificacao.length > 0 ? (
          <div style={{ fontSize: 13 }}>
            Rubricas do contracheque a classificar no Motor de Rubricas:{" "}
            <span style={{ fontFamily: "var(--font-mono)" }}>{d.rubricasSemClassificacao.join(", ")}</span>
          </div>
        ) : null}

        <DataTable
          caption="Base informada e base legal por servidor"
          columns={colunas}
          rows={linhas}
          rowKey={(s) => s.matricula}
          loading={q.isLoading}
          emptyState="Nenhum servidor ativo neste órgão."
        />
        {d && d.servidores.length > linhas.length ? (
          <div style={{ fontSize: 12, color: "var(--text-dim)" }}>
            Mostrando os {linhas.length} com maior diferença (e os sem base) de {d.servidores.length}. A ficha de cada
            servidor mostra o contracheque inteiro.
          </div>
        ) : null}
      </div>
    </Card>
  );
}
